import { Link } from "react-router-dom";
import { ArrowRight, Clock, Lock, ShieldCheck, Smartphone } from "lucide-react";
import { useGamesPaused, SOON_TEXT } from "../lib/useGamesPaused";
import { useSeo } from "../lib/seo";
import { Header } from "../components/Header";
import { PublicFooter } from "../components/PublicChrome";
import { GAMES } from "../lib/stb";
import { GameVideo } from "../components/stb/GameVideo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const Perk = ({ icon: Icon, title, text, link }) => (
  <div className="border-2 border-slate-900 bg-white p-4"><Icon className="h-5 w-5" /><div className="mt-2 font-bold">{title}</div><p className="text-sm text-slate-600">{text}</p>
    {link && <Link to={link[0]} className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-lime-700 underline hover:text-lime-800" data-testid="games-fair-link">{link[1]} <ArrowRight className="h-3.5 w-3.5" /></Link>}
  </div>
);

const Faq = ({ id, items }) => (
  <Accordion type="single" collapsible className="border-t-2 border-slate-900" data-testid={`game-faq-${id}`}>
    {items.map(([q, a], n) => (
      <AccordionItem key={q} value={q}>
        <AccordionTrigger className="text-left text-sm font-bold" data-testid={`game-faq-${id}-q${n}`}>{q}</AccordionTrigger>
        <AccordionContent className="text-sm text-slate-600">{a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

const GameCard = ({ id, g, i, paused }) => (
  <article className={`relative grid overflow-hidden border-2 border-slate-900 bg-white shadow-[6px_6px_0_0_#0f172a] md:grid-cols-[1.1fr_1fr] ${paused ? "" : "card-lift"}`} data-testid={`game-card-${id}`}>
    {paused && <span className="absolute left-3 top-3 z-10 border-2 border-slate-900 bg-amber-300 px-3 py-1 text-xs font-black uppercase shadow-[3px_3px_0_0_#0f172a]" data-testid={`game-soon-${id}`}>Coming soon</span>}
    <div className={paused ? "pointer-events-none select-none opacity-60 grayscale" : "contents"} aria-disabled={paused || undefined}><GameVideo id={id} g={g} eager={!i} /></div>
    <div className="space-y-3 p-5">
      <div className="eyebrow">Game {i + 1}</div>
      <h2 className="font-display text-3xl font-black uppercase leading-none">{g.name}</h2>
      <p className="font-semibold text-lime-700">{g.tag}</p>
      <ol className="space-y-2 text-sm">
        {g.steps.map((s, n) => <li key={s} className="flex gap-2"><span className="flex h-6 w-6 shrink-0 items-center justify-center bg-slate-900 text-xs font-black text-lime-300">{n + 1}</span>{s}</li>)}
      </ol>
      <Faq id={id} items={g.faq} />
      {paused
        ? <span className="inline-flex cursor-not-allowed items-center gap-2 border-2 border-slate-300 bg-slate-100 px-5 py-3 text-sm font-bold uppercase text-slate-400" aria-disabled="true" data-testid={`game-start-${id}`}>Coming soon</span>
        : <Link to={`/coach/stb/new?game=${id}`} className="btn-blaze inline-flex items-center gap-2 px-5 py-3 text-sm" data-testid={`game-start-${id}`}>Start a {g.name} <ArrowRight className="h-4 w-4" /></Link>}
    </div>
  </article>
);

export default function Games() {
  useSeo({ title: "Club Fundraising Games – Spot the Ball, Duck Race & More", description: "Raise money for your club with digital Spot the Ball, Score Squares and Duck Race. Share one WhatsApp link, parents pay on their phone, fair encrypted draws.", path: "/fundraising-games" });
  const paused = useGamesPaused();
  return (
    <div className="min-h-screen">
      <Header onNew={() => { window.location.href = "/design"; }} />
      <main className="mx-auto max-w-6xl space-y-10 px-4 py-10 sm:px-6">
        <section className="max-w-3xl">
          <div className="eyebrow">Club fundraising</div>
          <h1 className="font-display text-4xl font-black uppercase leading-none sm:text-5xl lg:text-6xl" data-testid="games-title">Fundraising games for your club</h1>
          <p className="mt-4 text-base text-slate-700 md:text-lg">Set up a game in two minutes, share one link in the team WhatsApp and watch the squares fill up. No app, no login for parents.</p>
        </section>
        <div className="grid gap-3 sm:grid-cols-3">
          <Perk icon={Smartphone} title="Built for phones" text="Parents tap and pay by card, Apple Pay or Google Pay – no cash to collect or count." />
          <Perk icon={ShieldCheck} title="Provably fair" text="The winner is picked at random and encrypted before anyone buys an entry, so nobody can change it." link={["/fair-draws", "What does this mean?"]} />
          <Perk icon={Lock} title="Money to your club" text="€12.99 to launch a game, or 3 games for €30. A 1% fee on money raised covers hosting and online play – the rest goes to your club by bank transfer." />
        </div>
        {paused && <div className="flex items-center gap-3 border-2 border-slate-900 bg-amber-100 p-4 font-bold" data-testid="games-soon-banner"><Clock className="h-5 w-5 shrink-0" /> {SOON_TEXT}</div>}
        <div className="space-y-8">{Object.entries(GAMES).map(([id, g], i) => <GameCard key={id} id={id} g={g} i={i} paused={paused} />)}</div>
        <p className="text-xs text-slate-500">Paid entrants must be 18+. Clubs are responsible for any lottery permit or licence their fundraiser needs.</p>
      </main>
      <PublicFooter />
    </div>
  );
}
